import { useMediaQuery } from "@mui/material";
import {
  BlockWrapper,
  defineBlock,
  smartProps,
  useEditor,
  useInspectorControl,
} from "@smartfire/js-block-engine";
import clsx from "clsx";
import he from "he";
import { lazy, useMemo, useState, useEffect } from "react";
import { Socials } from "../components";
import BurgerMenu from "./BurgerMenu";
import LangSwitcher from "./LangSwitcher";
import MenuMobile from "./MenuMobile";
import ScrollMenu from "./ScrollMenu";
import SearchBar from "./SearchBar";
import SocialsSticky from "./SocialsSticky";
import SubMenu from "./SubMenu";
import { ReactComponent as BurgerIcon } from "./assets/burger.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as Logo } from "../assets/logo.svg";

import style from "./block.module.scss";

const Inspector = lazy(() => import("./Inspector.js"));

function Header({
  socials,
  showSocialMedia,
  categories = [],
  contactLink,
  home,
  categoriesLink,
  langs,
  productSection = [],
  searchPageLink,
  desktopImage,
  mobileImage,
  storeLocatorPage,
  topCategoryMenu,
}) {
  useInspectorControl(Inspector);

  const { isEditing } = useEditor();
  const isTablet = useMediaQuery(`(max-width:${style.tabletBreakpoint})`);
  const isMobile = useMediaQuery(`(max-width:${style.mobileBreakpoint})`);
  const [burgerMenuOpen, setBurgerMenuOpen] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState(0);
  const [delay, setDelay] = useState(true);

  // Check if we are on the Homepage
  const isHomePage = window.location.pathname === "/";

  const allTerms = useMemo(
    () =>
      categories
        ? Object.values(categories).concat(productSection)
        : productSection,
    [categories, productSection]
  );

  const openSubMenu = (idCat) => {
    if (!delay) {
      document.getElementById("page").classList.add("dark-overlay");
      setHoveredCategory(idCat);
    }
  };

  const closeSubMenu = () => {
    if (delay) setDelay(false);
    setHoveredCategory(0);
    document.getElementById("page").classList.remove("dark-overlay");
  };

  const openBurgerMenu = () => {
    setBurgerMenuOpen(true);
    document.getElementById("page").classList.add("dark-overlay");
  };

  const closeBurgerMenu = () => {
    setBurgerMenuOpen(false);
    document.getElementById("page").classList.remove("dark-overlay");
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (delay) setDelay(false);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [delay]);

  return (
    <BlockWrapper.Header
      className={clsx(
        style.block,
        topCategoryMenu && isHomePage && style.isTopCategoryMenu
      )}
    >
      {!isMobile ? (
        <>
          <div className={style.mainHeader}>
            <div className={style.topBar}>
              <div className={style.content}>
                <div className={style.contact}>
                  <a href={contactLink.url}>{contactLink.label}</a>
                </div>

                <LangSwitcher langs={langs} className={style.langSwitcher} />

                <Socials socials={socials} className={style.socialNetworks} />
              </div>
            </div>

            <div className={style.container}>
              {isHomePage && <h1 className={style.title}>{document.title}</h1>}

              <div className={style.searchArea}>
                {isTablet && (
                  <div className={style.toggleBurgerMenuBtn}>
                    {!burgerMenuOpen ? (
                      <BurgerIcon
                        className={style.burgerIcon}
                        onClick={openBurgerMenu}
                      />
                    ) : (
                      <CloseIcon
                        className={style.closeIcon}
                        onClick={closeBurgerMenu}
                      />
                    )}
                  </div>
                )}

                <a href={home}>
                  {desktopImage?.url ? (
                    <img
                      src={desktopImage.url}
                      alt="Seezon Logo"
                      className={style.logo}
                      loading="lazy"
                    />
                  ) : (
                    <Logo className={style.logo} aria-label="Seezon Logo" />
                  )}
                </a>

                <SearchBar searchPageLink={searchPageLink} />
              </div>

              {!isTablet && (
                <div className={style.menu}>
                  <ul className={style.categories}>
                    {allTerms &&
                      allTerms.map((category) => (
                        <li
                          key={category.cat_ID}
                          className={style.category}
                          onMouseEnter={() =>
                            category.children && openSubMenu(category.cat_ID)
                          }
                          onMouseLeave={category.children && closeSubMenu}
                          onFocus={() =>
                            category.children && openSubMenu(category.cat_ID)
                          }
                          onBlur={(e) => {
                            if (
                              category.children &&
                              !e.currentTarget.contains(e.relatedTarget)
                            ) {
                              closeSubMenu();
                            }
                          }}
                        >
                          <a
                            href={category.url}
                            id={`menu-${category.cat_ID}`}
                            onFocus={() =>
                              category.children && openSubMenu(category.cat_ID)
                            }
                            aria-expanded={
                              category.children
                                ? category.cat_ID === hoveredCategory
                                : undefined
                            }
                            aria-controls={
                              category.children
                                ? `submenu-${category.cat_ID}`
                                : undefined
                            }
                          >
                            {he.decode(category.cat_name)}
                          </a>

                          {category.children && (
                            <div
                              key={category.cat_ID}
                              className={clsx(
                                style.subMenuContainer,
                                category.cat_ID === hoveredCategory &&
                                  style.open
                              )}
                              role="menu"
                              aria-labelledby={`menu-${category.cat_ID}`}
                            >
                              <SubMenu
                                categories={category.children}
                                onClick={closeSubMenu}
                                onMouseLeave={closeSubMenu}
                              />
                            </div>
                          )}
                        </li>
                      ))}
                  </ul>

                  {categoriesLink.label && (
                    <a href={categoriesLink.url} className={style.menuItem}>
                      {categoriesLink.label}
                    </a>
                  )}
                </div>
              )}
            </div>

            <BurgerMenu
              burgerMenuOpen={burgerMenuOpen}
              socials={socials}
              categories={allTerms}
              contactLink={contactLink}
              categoriesLink={categoriesLink}
              langs={langs}
            />
          </div>

          {!isEditing && (
            <ScrollMenu
              home={home}
              categories={allTerms}
              socials={socials}
              categoriesLink={categoriesLink}
            />
          )}

          {!isEditing && showSocialMedia && <SocialsSticky socials={socials} />}
        </>
      ) : (
        <MenuMobile
          home={home}
          logo={mobileImage}
          langs={langs}
          searchPageLink={searchPageLink}
          categories={categories}
          productSection={productSection}
          contactLink={contactLink}
          categoriesLink={categoriesLink}
          socials={socials}
          storeLocatorPage={storeLocatorPage}
          topCategoryMenu={topCategoryMenu}
        />
      )}
    </BlockWrapper.Header>
  );
}

export default defineBlock({
  Block: Header,
  smartProps: {
    desktopImage: smartProps.image,
    mobileImage: smartProps.image,
    contactLink: smartProps.object.withDefault({
      linkType: "other",
      url: "",
      pageId: 0,
      label: "Contact",
    }),
    categoriesLink: smartProps.object.withDefault({
      linkType: "other",
      url: "",
      pageId: 0,
      label: "Find your shop",
    }),
  },
  setup: {
    title: "Header",
  },
});
